import { cn } from "@/lib/utils";
import type { ComponentPropsWithoutRef } from "react";

interface MarqueeProps extends ComponentPropsWithoutRef<"div"> {
  reverse?: boolean;
  pauseOnHover?: boolean;
  /** How many copies of the children to render. Must be enough to overflow the container. */
  repeat?: number;
  duration?: string;
  gap?: string;
}

/**
 * CSS-only marquee (magicui pattern). Seamless with differently sized items:
 * the gap lives INSIDE each repeated group and the keyframe translates by
 * calc(-100% - gap) of the group's own width, so the loop point always aligns.
 */
export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  repeat = 4,
  duration = "30s",
  gap = "3.5rem",
  children,
  ...props
}: MarqueeProps) {
  return (
    <div
      {...props}
      className={cn("group flex overflow-hidden [gap:var(--gap)]", className)}
      style={{ "--duration": duration, "--gap": gap } as React.CSSProperties}
    >
      {Array.from({ length: repeat }, (_, i) => (
        <div
          key={i}
          aria-hidden={i > 0}
          className={cn(
            "flex shrink-0 items-center justify-around [gap:var(--gap)] animate-marquee motion-reduce:[animation-play-state:paused]",
            reverse && "[animation-direction:reverse]",
            pauseOnHover && "group-hover:[animation-play-state:paused]",
          )}
        >
          {children}
        </div>
      ))}
    </div>
  );
}
